import React, { useEffect } from "react";
import { Box, Card, Typography, Grid, Stack, Input, Select, Option, Chip, Divider, Checkbox, FormControl, FormHelperText } from "@mui/joy";
import { Wrench, PackageCheck, Clock } from "lucide-react";
import moment from "moment";
import { useProject } from "../../hooks";
import { useInspectionAttribute } from "./hooks";

import { ShingleType } from "../Inspection/components/ShingleType";
import { ColorSelection } from "../Inspection/components/ColorSelection";
import { RoofSlopes } from "../Inspection/components/RoofSlopes";
import { TearOff } from "../Inspection/components/TearOff";
import { UnderlaymentType } from "../Inspection/components/UnderlaymentType";
import { StarterType } from "../Inspection/components/StarterType";
import { RidgeCapProfile } from "../Inspection/components/RidgeCapProfile";
import { VentilationType } from "../Inspection/components/VentilationType";
import { VentilationPainted } from "../Inspection/components/VentilationPainted";
import { FurnaceVentSize } from "../Inspection/components/FurnaceVentSize";
import { EaveDripEdge } from "../Inspection/components/EaveDripEdge";
import { RakeDripEdge } from "../Inspection/components/RakeDripEdge";
import { ValleyType } from "../Inspection/components/ValleyType";
import { EvaporativeCooler } from "../Inspection/components/EvaporativeCooler";
import { GutterGuardType } from "../Inspection/components/GutterGuardType";
import { BuildingStories } from "../Inspection/components/BuildingStories";
import { ChimneyList } from "../Inspection/components/ChimneyList";
import { SkylightList } from "../Inspection/components/SkylightList";
import { PropertyPhotos } from "../Inspection/components/PropertyPhotos";
import { InitialInspectionReport } from "../Inspection/components/InitialInspectionReport";
import { ClaimsEstimateReport } from "../Inspection/components/ClaimsEstimateReport";
import { TaskActionComponent } from "../Tasks/components";

type ActionType = "R&R" | "Remove" | "Install" | "D&R";
const ACTION_OPTIONS: ActionType[] = ["R&R", "Remove", "Install", "D&R"];

interface IInspectionV2 {
    activeTask: any;
    attributes: any[];
    setAttributes: (e: any) => void;
    measurements: any[];
    setMeasurements: (e: any) => void;
    onComplete: () => void;
}

export default function InspectionV2(props: IInspectionV2) {
    const { activeTask, attributes, setAttributes, onComplete } = props;
    const { project } = useProject();
    const { actions, handleVisualInput, handleActionChange, handleUpdate, getAttrValue, getVisualItem } =
        useInspectionAttribute({ project, attributes, setAttributes });

    // Auto-default Starter Type based on Shingle Type
    useEffect(() => {
        const shingleType = getAttrValue("Shingle Type");
        const currentStarter = getAttrValue("Starter Type");

        if (shingleType === "Shake Look") {
            if (currentStarter !== "Laminated Double Layer") {
                handleUpdate("Laminated Double Layer", "Starter Type");
            }
        } else if (shingleType && shingleType !== "None") {
            // Default others to Universal Starter if a shingle is selected
            if (currentStarter !== "Universal Starter") {
                handleUpdate("Universal Starter", "Starter Type");
            }
        }
    }, [getAttrValue("Shingle Type")]);

    const sectionHeaderSx = {
        color: "text.tertiary",
        mb: 1.5,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontSize: "11px",
        fontWeight: "xl",
    };
    const cardSx = { p: 2, boxShadow: "none" };

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                {/* TASK HEADER — same as V1 */}
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography level="h4" sx={{ fontWeight: 500, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                    Initial Roof Inspection
                </Typography>

                <Box mt={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Chip
                        color={activeTask?.Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {activeTask?.Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(activeTask?.CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                </Box>

                {/* DESCRIPTION */}
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Description
                    </Typography>
                    <Typography level="body-md" sx={{ color: "text.primary" }}>
                        The primary goal of an Initial Roof Inspection is to assess the overall condition of a roof,
                        identify potential issues, document findings, and prepare a detailed report. This inspection
                        serves as a baseline evaluation for maintenance, repair, or replacement decisions.
                    </Typography>
                </Box>

                {/* PHOTOS — directly, no card wrapper */}
                <Box mt={2}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Photos
                    </Typography>
                    <Typography level="body-md" sx={{ color: "text.primary" }}>
                        Upload clear and detailed photos taken during the initial roof inspection to document its
                        current condition. These images will help assess any existing damages, structural concerns, or
                        areas requiring maintenance.
                    </Typography>
                    <Box mt={1}>
                        <PropertyPhotos taskDetails={activeTask} />
                    </Box>
                </Box>

                {/* WORKSHEET SECTIONS */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 4 }}>
                    {/* GROUP: SHINGLE & ROOFING */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Shingle & Roofing
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={3}>
                                    <ShingleType
                                        value={getAttrValue("Shingle Type")}
                                        onChange={(v) => handleUpdate(v, "Shingle Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <ColorSelection
                                        value={getAttrValue("Roof Color")}
                                        onChange={(v) => handleUpdate(v, "Roof Color")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <TearOff
                                        value={getAttrValue("Tear Off Layer")}
                                        onChange={(v) => handleUpdate(v, "Tear Off Layer")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Low Slope (SQ)"
                                        item={getVisualItem("Low Slope") || { name: "Low Slope", actual: 0, id: null }}
                                        action={actions["Low Slope"]}
                                        onQtyChange={(v) => handleVisualInput("Low Slope", v)}
                                        onActionChange={(a) => handleActionChange("Low Slope", a)}
                                        unit="SQ"
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 1, opacity: 0.6 }} />

                            <Grid container spacing={2}>
                                <Grid xs={12} sm={6} md={3}>
                                    <StarterType
                                        value={getAttrValue("Starter Type")}
                                        onChange={(v) => handleUpdate(v, "Starter Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <RidgeCapProfile
                                        value={getAttrValue("Ridge Cap Profile")}
                                        onChange={(v) => handleUpdate(v, "Ridge Cap Profile")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <BuildingStories
                                        value={getAttrValue("Stories")}
                                        onChange={(v) => handleUpdate(v, "Stories")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="High Roof (SQ)"
                                        item={getVisualItem("High Roof") || { name: "High Roof", actual: 0, id: null }}
                                        action={actions["High Roof"]}
                                        onQtyChange={(v) => handleVisualInput("High Roof", v)}
                                        onActionChange={(a) => handleActionChange("High Roof", a)}
                                        unit="SQ"
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 1, opacity: 0.6 }} />

                            <Grid container spacing={3}>
                                <Grid xs={12} sm={12} md={12}>
                                    <RoofSlopes
                                        value={getAttrValue("Roof Pitch")}
                                        onChange={(v) => handleUpdate(v, "Roof Pitch")}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: UNDERLAYMENT & SHIELD */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Underlayment & Shield
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={6}>
                                    <UnderlaymentType
                                        value={getAttrValue("Underlayment Type")}
                                        onChange={(v) => handleUpdate(v, "Underlayment Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={6}>
                                    <CountWithAction
                                        label="Ice & Water Barrier"
                                        item={
                                            getVisualItem("Ice & Water Barrier") || {
                                                name: "Ice & Water Barrier",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Ice & Water Barrier"]}
                                        onQtyChange={(v) => handleVisualInput("Ice & Water Barrier", v)}
                                        onActionChange={(a) => handleActionChange("Ice & Water Barrier", a)}
                                        unit="SF"
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: VENTILATION */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Ventilation
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3} alignItems="flex-end">
                                <Grid xs={12} sm={6} md={4}>
                                    <VentilationType
                                        value={getAttrValue("Ventilation Type")}
                                        onChange={(v) => handleUpdate(v, "Ventilation Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <VentilationPainted
                                        value={getAttrValue("Ventilation Painted")}
                                        onChange={(v) => handleUpdate(v, "Ventilation Painted")}
                                        showNA={getAttrValue("Ventilation Type") === "Ridge Vent"}
                                    />
                                </Grid>
                                {/* Conditionally show LF or Count based on vent type */}
                                {getAttrValue("Ventilation Type") === "Ridge Vent" ? (
                                    <Grid xs={12} sm={6} md={4}>
                                        <CountWithAction
                                            label="Ridge Vent (LF)"
                                            item={getVisualItem("Roof Vent")}
                                            action={actions["Roof Vent"]}
                                            onQtyChange={(v) => handleVisualInput("Roof Vent", v)}
                                            onActionChange={(a) => handleActionChange("Roof Vent", a)}
                                            unit="LF"
                                        />
                                    </Grid>
                                ) : (
                                    <Grid xs={12} sm={6} md={4}>
                                        <CountWithAction
                                            label="Box Vents"
                                            item={getVisualItem("Roof Vent")}
                                            action={actions["Roof Vent"]}
                                            onQtyChange={(v) => handleVisualInput("Roof Vent", v)}
                                            onActionChange={(a) => handleActionChange("Roof Vent", a)}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: PENETRATIONS */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Penetrations
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={12} md={6}>
                                    <FurnaceVentSize
                                        value={getAttrValue("Furnace Vent Size")}
                                        onChange={(v) => handleUpdate(v, "Furnace Vent Size")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Pipe Jack"
                                        item={getVisualItem("Pipe Jack")}
                                        action={actions["Pipe Jack"]}
                                        onQtyChange={(v) => handleVisualInput("Pipe Jack", v)}
                                        onActionChange={(a) => handleActionChange("Pipe Jack", a)}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Split Boots"
                                        item={getVisualItem("Split Boots")}
                                        action={actions["Split Boots"]}
                                        onQtyChange={(v) => handleVisualInput("Split Boots", v)}
                                        onActionChange={(a) => handleActionChange("Split Boots", a)}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: DRIP EDGE, FLASHING & VALLEY */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Drip Edge, Flashing & Valley
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={4}>
                                    <EaveDripEdge
                                        value={getAttrValue("Eave Drip Edge")}
                                        onChange={(v) => handleUpdate(v, "Eave Drip Edge")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <RakeDripEdge
                                        value={getAttrValue("Rake Drip Edge")}
                                        onChange={(v) => handleUpdate(v, "Rake Drip Edge")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <ValleyType
                                        value={getAttrValue("Valley Type")}
                                        onChange={(v) => handleUpdate(v, "Valley Type")}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 1, opacity: 0.6 }} />

                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={4}>
                                    <CountWithAction
                                        label="Step Flashing"
                                        item={
                                            getVisualItem("Step Flashing") || {
                                                name: "Step Flashing",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Step Flashing"]}
                                        onQtyChange={(v) => handleVisualInput("Step Flashing", v)}
                                        onActionChange={(a) => handleActionChange("Step Flashing", a)}
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <CountWithAction
                                        label="Counter Flashing"
                                        item={
                                            getVisualItem("Counter Flashing") || {
                                                name: "Counter Flashing",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Counter Flashing"]}
                                        onQtyChange={(v) => handleVisualInput("Counter Flashing", v)}
                                        onActionChange={(a) => handleActionChange("Counter Flashing", a)}
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <CountWithAction
                                        label="Kick-out Diverter"
                                        item={
                                            getVisualItem("Kick-out Diverter") || {
                                                name: "Kick-out Diverter",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Kick-out Diverter"]}
                                        onQtyChange={(v) => handleVisualInput("Kick-out Diverter", v)}
                                        onActionChange={(a) => handleActionChange("Kick-out Diverter", a)}
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: CHIMNEY */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Chimney
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <ChimneyList
                                value={getAttrValue("Chimney Details")}
                                onChange={(v) => handleUpdate(v, "Chimney Details")}
                            />
                        </Card>
                    </Box>

                    {/* GROUP: SKYLIGHT */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Skylight
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <SkylightList
                                value={getAttrValue("Skylight Details")}
                                onChange={(v) => handleUpdate(v, "Skylight Details")}
                            />
                        </Card>
                    </Box>

                    {/* GROUP: EQUIPMENT */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Equipment
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={4}>
                                    <EvaporativeCooler
                                        value={getAttrValue("Has Evaporative Cooler")}
                                        onChange={(v) => handleUpdate(v, "Has Evaporative Cooler")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <GutterGuardType
                                        value={getAttrValue("Gutter Guard Type")}
                                        onChange={(v) => handleUpdate(v, "Gutter Guard Type")}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={4}>
                                    <CountWithAction
                                        label="Paint Gutter"
                                        item={
                                            getVisualItem("Paint Gutter") || {
                                                name: "Paint Gutter",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Paint Gutter"]}
                                        onQtyChange={(v) => handleVisualInput("Paint Gutter", v)}
                                        onActionChange={(a) => handleActionChange("Paint Gutter", a)}
                                        unit="LF"
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>

                    {/* GROUP: GUTTERS & DOWNSPOUTS */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Gutters &amp; Downspouts
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Gutter Size</Typography>
                                    <Select
                                        size="sm"
                                        placeholder="Select size"
                                        value={getAttrValue("Gutter Size") || null}
                                        onChange={(_e, v) => handleUpdate(v || "", "Gutter Size")}
                                        slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                                    >
                                        <Option value='5"'>5"</Option>
                                        <Option value='6"'>6"</Option>
                                    </Select>
                                </Grid>

                                <Grid xs={12} sm={6} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Gutter Color</Typography>
                                    <Input
                                        size="sm"
                                        placeholder="e.g. White"
                                        value={getAttrValue("Gutter Color") || ""}
                                        onChange={(e) => handleUpdate(e.target.value, "Gutter Color")}
                                    />
                                </Grid>

                                <Grid xs={12} sm={12} md={6}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Gutter Length</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Input
                                            size="sm"
                                            type="number"
                                            placeholder="0"
                                            value={getAttrValue("Gutter Length") || ""}
                                            onChange={(e) => handleUpdate(e.target.value, "Gutter Length")}
                                            endDecorator={<Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: "bold" }}>LF</Typography>}
                                            slotProps={{ input: { min: 0 } }}
                                            sx={{ flex: 1 }}
                                        />
                                        <Select
                                            size="sm"
                                            variant="outlined"
                                            value={
                                                getAttrValue("Remove Existing Gutters") === "true" && getAttrValue("Install New Gutters") === "true" ? "R&R" :
                                                getAttrValue("Remove Existing Gutters") === "true" ? "Remove" :
                                                getAttrValue("Install New Gutters") === "true" ? "Install" :
                                                "R&R" // default
                                            }
                                            onChange={(_e, val) => {
                                                if (val === "R&R") {
                                                    handleUpdate("true", "Remove Existing Gutters");
                                                    handleUpdate("true", "Install New Gutters");
                                                } else if (val === "Remove") {
                                                    handleUpdate("true", "Remove Existing Gutters");
                                                    handleUpdate("false", "Install New Gutters");
                                                } else if (val === "Install") {
                                                    handleUpdate("false", "Remove Existing Gutters");
                                                    handleUpdate("true", "Install New Gutters");
                                                } else if (val === "D&R") {
                                                    handleUpdate("false", "Remove Existing Gutters");
                                                    handleUpdate("false", "Install New Gutters");
                                                }
                                            }}
                                            slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                                            sx={{ width: 90 }}
                                        >
                                            <Option value="R&R">R&R</Option>
                                            <Option value="Remove">Remove</Option>
                                            <Option value="Install">Install</Option>
                                            <Option value="D&R">D&R</Option>
                                        </Select>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 1.5, opacity: 0.6 }} />

                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Downspout Size</Typography>
                                    <Select
                                        size="sm"
                                        placeholder="Select size"
                                        value={getAttrValue("Downspout Size") || null}
                                        onChange={(_e, v) => handleUpdate(v || "", "Downspout Size")}
                                        slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                                    >
                                        <Option value='2x3"'>2×3"</Option>
                                        <Option value='3x4"'>3×4"</Option>
                                    </Select>
                                </Grid>

                                <Grid xs={12} sm={6} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Downspout Color</Typography>
                                    <Input
                                        size="sm"
                                        placeholder="e.g. White"
                                        value={getAttrValue("Downspout Color") || ""}
                                        onChange={(e) => handleUpdate(e.target.value, "Downspout Color")}
                                    />
                                </Grid>

                                <Grid xs={12} sm={12} md={6}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Downspout Length</Typography>
                                    <Stack direction="row" spacing={1}>
                                        <Input
                                            size="sm"
                                            type="number"
                                            placeholder="0"
                                            value={getAttrValue("Downspout Length") || ""}
                                            onChange={(e) => handleUpdate(e.target.value, "Downspout Length")}
                                            endDecorator={<Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: "bold" }}>LF</Typography>}
                                            slotProps={{ input: { min: 0 } }}
                                            sx={{ flex: 1 }}
                                        />
                                        <Select
                                            size="sm"
                                            variant="outlined"
                                            value={
                                                getAttrValue("Remove Existing Downspouts") === "true" && getAttrValue("Install New Downspouts") === "true" ? "R&R" :
                                                getAttrValue("Remove Existing Downspouts") === "true" ? "Remove" :
                                                getAttrValue("Install New Downspouts") === "true" ? "Install" :
                                                "R&R" // default
                                            }
                                            onChange={(_e, val) => {
                                                if (val === "R&R") {
                                                    handleUpdate("true", "Remove Existing Downspouts");
                                                    handleUpdate("true", "Install New Downspouts");
                                                } else if (val === "Remove") {
                                                    handleUpdate("true", "Remove Existing Downspouts");
                                                    handleUpdate("false", "Install New Downspouts");
                                                } else if (val === "Install") {
                                                    handleUpdate("false", "Remove Existing Downspouts");
                                                    handleUpdate("true", "Install New Downspouts");
                                                } else if (val === "D&R") {
                                                    handleUpdate("false", "Remove Existing Downspouts");
                                                    handleUpdate("false", "Install New Downspouts");
                                                }
                                            }}
                                            slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                                            sx={{ width: 90 }}
                                        >
                                            <Option value="R&R">R&R</Option>
                                            <Option value="Remove">Remove</Option>
                                            <Option value="Install">Install</Option>
                                            <Option value="D&R">D&R</Option>
                                        </Select>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>



                    {/* GROUP: ANCILLARY / TRIM */}
                    <Box>
                        <Typography level="title-sm" sx={sectionHeaderSx}>
                            Ancillary / Trim
                        </Typography>
                        <Card variant="soft" sx={cardSx}>
                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Cornice Returns"
                                        item={
                                            getVisualItem("Cornice Returns") || {
                                                name: "Cornice Returns",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Cornice Returns"]}
                                        onQtyChange={(v) => handleVisualInput("Cornice Returns", v)}
                                        onActionChange={(a) => handleActionChange("Cornice Returns", a)}
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Cornice Strip"
                                        item={
                                            getVisualItem("Cornice Strip") || {
                                                name: "Cornice Strip",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Cornice Strip"]}
                                        onQtyChange={(v) => handleVisualInput("Cornice Strip", v)}
                                        onActionChange={(a) => handleActionChange("Cornice Strip", a)}
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <CountWithAction
                                        label="Fascia Board"
                                        item={
                                            getVisualItem("Fascia Board") || {
                                                name: "Fascia Board",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Fascia Board"]}
                                        onQtyChange={(v) => handleVisualInput("Fascia Board", v)}
                                        onActionChange={(a) => handleActionChange("Fascia Board", a)}
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={3}>
                                    <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>Soffit</Typography>
                                    <Input
                                        size="sm"
                                        type="number"
                                        placeholder="0"
                                        value={getAttrValue("Soffit") || ""}
                                        onChange={(e) => handleUpdate(e.target.value, "Soffit")}
                                        endDecorator={<Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: "bold" }}>Inches</Typography>}
                                        slotProps={{ input: { min: 0 } }}
                                    />
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 1, opacity: 0.6 }} />

                            <Grid container spacing={3}>
                                <Grid xs={12} sm={6} md={6}>
                                    <CountWithAction
                                        label="Gutter Apron"
                                        item={
                                            getVisualItem("Gutter Apron") || {
                                                name: "Gutter Apron",
                                                actual: 0,
                                                id: null,
                                            }
                                        }
                                        action={actions["Gutter Apron"]}
                                        onQtyChange={(v) => handleVisualInput("Gutter Apron", v)}
                                        onActionChange={(a) => handleActionChange("Gutter Apron", a)}
                                        unit="LF"
                                    />
                                </Grid>
                                <Grid xs={12} sm={6} md={6}>
                                    <CountWithAction
                                        label="Sheathing / OSB"
                                        item={getVisualItem("Sheathing") || { name: "Sheathing", actual: 0, id: null }}
                                        action={actions["Sheathing"]}
                                        onQtyChange={(v) => handleVisualInput("Sheathing", v)}
                                        onActionChange={(a) => handleActionChange("Sheathing", a)}
                                        unit="SF"
                                    />
                                </Grid>
                            </Grid>
                        </Card>
                    </Box>
                </Box>

                {/* DOCUMENTS */}
                <Box mt={4}>
                    <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Documents Review
                    </Typography>
                    <Typography level={"body-md"} sx={{ color: "text.primary" }} gutterBottom>
                        Review and download the generated inspection reports and estimates.
                    </Typography>
                    <Box mt={2} display="flex" flexWrap="wrap" gap={2}>
                        <InitialInspectionReport />
                        <ClaimsEstimateReport />
                    </Box>
                </Box>

                {/* TASK ACTION — complete task */}
                <TaskActionComponent taskName="Initial Inspection" onComplete={onComplete} isNextEligible={true} />
            </Box>
        </Box>
    );
}

/** Count input with an action override chip — visually matches Select/Input fields */
function CountWithAction({
    label,
    item,
    action,
    onQtyChange,
    onActionChange,
    unit,
}: {
    label: string;
    item: any;
    action: ActionType;
    onQtyChange: (v: number) => void;
    onActionChange: (a: ActionType) => void;
    unit?: string;
}) {
    return (
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>
                {label}
            </Typography>
            <Stack direction="row" spacing={1}>
                <Input
                    size="sm"
                    type="number"
                    placeholder="0"
                    value={item?.actual || ""}
                    onChange={(e) => onQtyChange(Math.max(0, Number(e.target.value) || 0))}
                    slotProps={{ input: { min: 0 } }}
                    endDecorator={
                        unit ? (
                            <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: "bold" }}>
                                {unit}
                            </Typography>
                        ) : undefined
                    }
                    sx={{ flex: 1 }}
                />
                <Select
                    size="sm"
                    variant="outlined"
                    value={action}
                    onChange={(_e, val) => val && onActionChange(val as ActionType)}
                    slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                    startDecorator={<Wrench size={14} />}
                    sx={{
                        minWidth: 100,
                        backgroundColor: "background.surface",
                    }}
                >
                    {ACTION_OPTIONS.map((opt) => (
                        <Option key={opt} value={opt}>
                            {opt}
                        </Option>
                    ))}
                </Select>
            </Stack>
        </Box>
    );
}
